import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminPlanPricing — embeddable inline in AdminDashboard.
 * Backend already enforces admin auth on /api/admin/plans endpoints.
 */
const AdminPlanPricing = () => {
  const [plans, setPlans] = useState([]);
  const [meta, setMeta] = useState({ gst_rate: 0.18, usd_rate: 83 });
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const authHeader = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/plans`, { headers: authHeader() });
      setPlans(res.data.plans || []);
      setMeta({
        gst_rate: res.data.gst_rate ?? 0.18,
        usd_rate: res.data.usd_rate ?? 83,
      });
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (id, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value === '' ? '' : Number(value) },
    }));
  };

  const currentValue = (plan, field) => {
    const edit = edits[plan.id]?.[field];
    if (edit !== undefined) return edit;
    return field === 'base_inr' ? plan.base_inr : plan.base_usd;
  };

  const computeTotals = (baseInr, baseUsd) => {
    const gstInr = Math.round(baseInr * meta.gst_rate * 100) / 100;
    const totalInr = Math.round((baseInr + gstInr) * 100) / 100;
    const gstUsd = Math.round(baseUsd * meta.gst_rate * 100) / 100;
    const totalUsd = Math.round((baseUsd + gstUsd) * 100) / 100;
    return { gstInr, totalInr, gstUsd, totalUsd };
  };

  const publish = async (plan) => {
    const edit = edits[plan.id];
    if (!edit) return;
    try {
      setSavingId(plan.id);
      const payload = {
        base_inr:
          edit.base_inr !== undefined && edit.base_inr !== '' ? edit.base_inr : plan.base_inr,
        base_usd:
          edit.base_usd !== undefined && edit.base_usd !== '' ? edit.base_usd : null,
      };
      const res = await axios.put(`${API}/admin/plans/${plan.id}`, payload, {
        headers: authHeader(),
      });
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? res.data.plan : p)));
      setEdits((prev) => {
        const { [plan.id]: _, ...rest } = prev;
        return rest;
      });
      toast.success(`${plan.name} published`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setSavingId(null);
    }
  };

  const reset = async (plan) => {
    if (!window.confirm(`Reset "${plan.name}" to default pricing?`)) return;
    try {
      setSavingId(plan.id);
      const res = await axios.delete(`${API}/admin/plans/${plan.id}`, {
        headers: authHeader(),
      });
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? res.data.plan : p)));
      setEdits((prev) => {
        const { [plan.id]: _, ...rest } = prev;
        return rest;
      });
      toast.success(`${plan.name} reset to default`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    } finally {
      setSavingId(null);
    }
  };

  const groups = plans.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900" data-testid="admin-plan-pricing-title">
            Plan Pricing
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            GST fixed at {(meta.gst_rate * 100).toFixed(0)}%. USD auto = INR / ₹{meta.usd_rate}.
          </p>
        </div>
        <button
          onClick={fetchPlans}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-amber-600">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading plans…
        </div>
      ) : (
        <div>
          {Object.entries(groups).map(([category, rows]) => (
            <div key={category}>
              <div className="px-4 py-2 bg-amber-50 border-y border-amber-100 flex items-center justify-between">
                <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                  {category}
                </h3>
                <span className="text-[10px] text-gray-500">
                  {rows.length} plan{rows.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Plan</th>
                      <th className="text-right px-2 py-2 font-semibold">Base ₹</th>
                      <th className="text-right px-2 py-2 font-semibold">GST ₹</th>
                      <th className="text-right px-2 py-2 font-semibold bg-amber-50">Total ₹</th>
                      <th className="text-right px-2 py-2 font-semibold">Base $</th>
                      <th className="text-right px-2 py-2 font-semibold">GST $</th>
                      <th className="text-right px-2 py-2 font-semibold bg-amber-50">Total $</th>
                      <th className="text-center px-2 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((plan) => {
                      const editing = edits[plan.id] !== undefined;
                      const baseInr = Number(currentValue(plan, 'base_inr')) || 0;
                      const baseUsd =
                        Number(currentValue(plan, 'base_usd')) ||
                        Math.round((baseInr / meta.usd_rate) * 100) / 100;
                      const { gstInr, totalInr, gstUsd, totalUsd } = computeTotals(
                        baseInr,
                        baseUsd
                      );
                      const isSaving = savingId === plan.id;
                      return (
                        <tr key={plan.id} className={editing ? 'bg-amber-50/50' : ''}>
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium text-gray-900 text-sm">{plan.name}</div>
                            <div className="text-[10px] text-gray-500 font-mono">{plan.id}</div>
                            {plan.override && !editing && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                                <CheckCircle2 className="w-2.5 h-2.5" /> Override
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <input
                              type="number"
                              value={currentValue(plan, 'base_inr')}
                              onChange={(e) => handleChange(plan.id, 'base_inr', e.target.value)}
                              className="w-20 text-right px-1.5 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-amber-500"
                              min="0"
                              step="1"
                              data-testid={`base-inr-${plan.id}`}
                            />
                          </td>
                          <td className="px-2 py-2 text-right text-gray-600 text-xs tabular-nums">
                            ₹{gstInr.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold text-gray-900 text-xs bg-amber-50/50 tabular-nums">
                            ₹{totalInr.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <input
                              type="number"
                              value={edits[plan.id]?.base_usd ?? (plan.base_usd ?? '')}
                              onChange={(e) => handleChange(plan.id, 'base_usd', e.target.value)}
                              placeholder="auto"
                              className="w-20 text-right px-1.5 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-amber-500"
                              min="0"
                              step="0.01"
                              data-testid={`base-usd-${plan.id}`}
                            />
                          </td>
                          <td className="px-2 py-2 text-right text-gray-600 text-xs tabular-nums">
                            ${gstUsd.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold text-gray-900 text-xs bg-amber-50/50 tabular-nums">
                            ${totalUsd.toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-center whitespace-nowrap">
                            <button
                              onClick={() => publish(plan)}
                              disabled={!editing || isSaving}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-600 text-white text-[10px] font-semibold disabled:opacity-40 hover:bg-amber-500"
                              data-testid={`publish-${plan.id}`}
                            >
                              {isSaving ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Save className="w-3 h-3" />
                              )}
                              Publish
                            </button>
                            {plan.override && (
                              <button
                                onClick={() => reset(plan)}
                                disabled={isSaving}
                                className="ml-1 inline-flex items-center gap-1 px-1.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-[10px] font-semibold hover:bg-gray-200 disabled:opacity-40"
                                title="Reset to default"
                                data-testid={`reset-${plan.id}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPlanPricing;
